import { useState } from 'react';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Switch,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { EmployerDocUploadField } from '../../components/EmployerDocUploadField';
import { testIds } from '../../testIds';
import type { UploadedDoc } from '../../AddEmployerModal/types';
import type { EmployerDetail } from '../types';

export type StatusPayload = {
  placementCapacity: number | null;
  excludedAt: Date | null;
  excludedReason: string | null;
  agreementFileDrns: string[];
  agreementExpiresAt: Date | null;
  insuranceFileDrns: string[];
  insuranceExpiresAt: Date | null;
};

function toDateInput(date: Date | null): string {
  return date != null ? djs(date).format('YYYY-MM-DD') : '';
}

function firstDocOrNull(drns: string[], filename: string): UploadedDoc | null {
  const [drn] = drns;
  return drn != null ? { filename, drn } : null;
}

export function StatusEditForm({
  employer,
  onSave,
  onCancel,
  isSaving,
}: {
  employer: EmployerDetail;
  onSave: (payload: StatusPayload) => void;
  onCancel: () => void;
  isSaving: boolean;
}): React.ReactElement {
  const today = djs().format('YYYY-MM-DD');

  const [capacity, setCapacity] = useState(
    employer.placementCapacity != null ? String(employer.placementCapacity) : '',
  );
  const [ready, setReady] = useState(employer.compliant);
  const [agreement, setAgreement] = useState<UploadedDoc | null>(() =>
    firstDocOrNull(employer.agreementFileDrns, t`Uploaded document`),
  );
  const [agreementExpiry, setAgreementExpiry] = useState(() =>
    toDateInput(employer.agreementExpiresAt),
  );
  const [insurance, setInsurance] = useState<UploadedDoc | null>(() =>
    firstDocOrNull(employer.insuranceFileDrns, t`Uploaded document`),
  );
  const [insuranceExpiry, setInsuranceExpiry] = useState(() =>
    toDateInput(employer.insuranceExpiresAt),
  );
  const [excluded, setExcluded] = useState(employer.excludedAt != null);
  const [reason, setReason] = useState(employer.excludedReason ?? '');
  const [showErrors, setShowErrors] = useState(false);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedCapacity = capacity.trim();
    if (
      ready &&
      (agreement == null || agreementExpiry < today || insurance == null || insuranceExpiry < today)
    ) {
      setShowErrors(true);
      return;
    }
    const documents = ready
      ? {
          agreementFileDrns: agreement != null ? [agreement.drn] : [],
          agreementExpiresAt: agreementExpiry !== '' ? new Date(agreementExpiry) : null,
          insuranceFileDrns: insurance != null ? [insurance.drn] : [],
          insuranceExpiresAt: insuranceExpiry !== '' ? new Date(insuranceExpiry) : null,
        }
      : {
          agreementFileDrns: employer.agreementFileDrns,
          agreementExpiresAt: employer.agreementExpiresAt,
          insuranceFileDrns: employer.insuranceFileDrns,
          insuranceExpiresAt: employer.insuranceExpiresAt,
        };
    onSave({
      placementCapacity: trimmedCapacity === '' ? null : Number(trimmedCapacity),
      excludedAt: excluded ? (employer.excludedAt ?? new Date()) : null,
      excludedReason: excluded && reason.trim() !== '' ? reason.trim() : null,
      ...documents,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%', height: '100%' }} noValidate>
      <FormMainContainer>
        <FormContentContainer>
          <VStack align="stretch" spacing={4} w="full" pb={4}>
            <Heading fontSize="lg" size="xs" w="full">
              <Trans>Edit status</Trans>
            </Heading>

            <FormControl>
              <FormLabel>
                <Trans>Placement capacity</Trans>
              </FormLabel>
              <NumberInput min={0} step={1} precision={0} value={capacity} onChange={setCapacity}>
                <NumberInputField data-testid={testIds.employerCapacityInput} />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <Text fontWeight="semibold" pt={2}>
              <Trans>Is this employer ready to accept student placements?</Trans>
            </Text>
            <Text fontSize="sm" color="fg.subtle">
              <Trans>
                All businesses must have a signed agreement and provided a current certificate of
                insurance before accepting student placements
              </Trans>
            </Text>

            <Switch
              colorScheme="brand"
              isChecked={ready}
              onChange={(event) => setReady(event.target.checked)}
              data-testid={testIds.readyForPlacementsToggle}
              display="flex"
              alignItems="center"
            >
              <Trans>Ready for placements</Trans>
            </Switch>

            {ready ? (
              <VStack align="stretch" spacing={4} w="full">
                <Text fontSize="sm" color="fg.subtle">
                  <Trans>Please upload the required documents</Trans>
                </Text>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={4} spacingY={4}>
                  <EmployerDocUploadField
                    label={<Trans>Upload agreement</Trans>}
                    kind="EMPLOYER_AGREEMENT"
                    doc={agreement}
                    onUploaded={setAgreement}
                    onClear={() => setAgreement(null)}
                    isInvalid={showErrors && agreement == null}
                    errorMessage={<Trans>Upload the signed agreement</Trans>}
                    data-testid={testIds.agreementUpload}
                  />
                  <FormControl isRequired isInvalid={showErrors && agreementExpiry < today}>
                    <FormLabel>
                      <Trans>Agreement expiry</Trans>
                    </FormLabel>
                    <Input
                      type="date"
                      min={today}
                      value={agreementExpiry}
                      onChange={(event) => setAgreementExpiry(event.target.value)}
                      data-testid={testIds.agreementExpiryInput}
                    />
                    <FormErrorMessage>
                      <Trans>Set an agreement expiry date of today or later</Trans>
                    </FormErrorMessage>
                  </FormControl>

                  <EmployerDocUploadField
                    label={<Trans>Upload certificate of insurance</Trans>}
                    kind="EMPLOYER_INSURANCE"
                    doc={insurance}
                    onUploaded={setInsurance}
                    onClear={() => setInsurance(null)}
                    isInvalid={showErrors && insurance == null}
                    errorMessage={<Trans>Upload the certificate of insurance</Trans>}
                    data-testid={testIds.insuranceUpload}
                  />
                  <FormControl isRequired isInvalid={showErrors && insuranceExpiry < today}>
                    <FormLabel>
                      <Trans>Insurance expiry</Trans>
                    </FormLabel>
                    <Input
                      type="date"
                      min={today}
                      value={insuranceExpiry}
                      onChange={(event) => setInsuranceExpiry(event.target.value)}
                      data-testid={testIds.insuranceExpiryInput}
                    />
                    <FormErrorMessage>
                      <Trans>Set an insurance expiry date of today or later</Trans>
                    </FormErrorMessage>
                  </FormControl>
                </SimpleGrid>
              </VStack>
            ) : null}

            <Switch
              colorScheme="brand"
              isChecked={excluded}
              onChange={(event) => setExcluded(event.target.checked)}
              data-testid={testIds.doNotUseToggle}
              display="flex"
              alignItems="center"
              pt={2}
            >
              <Trans>Do not use this employer</Trans>
            </Switch>

            {excluded ? (
              <FormControl>
                <FormLabel>
                  <Trans>Reason</Trans>
                </FormLabel>
                <Textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={t`Why is this employer excluded?`}
                />
              </FormControl>
            ) : null}
          </VStack>
        </FormContentContainer>

        <FormFooterContainer>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSaving}
            data-testid={testIds.saveEmployerButton}
          >
            <Trans>Save</Trans>
          </Button>
          <Button variant="secondary" onClick={onCancel} isDisabled={isSaving}>
            <Trans>Cancel</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    </form>
  );
}
